import { Box, Button, Theme, Typography } from '@mui/material';
import { errorMessages } from '~/constants/errorMessages.ts';
import { formatDateForDisplay } from '~/helpers/utils';
import { StatusChipWithTooltip } from '~/modules/covenants/components/StatusChipWithTooltip.tsx';
import { APP_STATUS_MAP } from '~/modules/covenants/statusConfig.ts';
import { covConstantsStyles } from '~/modules/covenants/constantsStyles.ts';
import {
  TApiRequiredDocument,
  TSignatureValidationDocumentState,
  StatusFilterValue,
  TApplicationStatus,
  TColumnDef,
} from '~/modules/covenants/types.ts';
import { getDocumentStatusConfig } from '~/modules/covenants/utils.ts';
import { isPastDeadline, mergeSx } from '~/modules/covenants/tableUtils.ts';

export type TStatusModal = {
  open: boolean;
  type: 'success' | 'error';
  title?: string;
  message: string;
  buttonValue?: string;
  onClick?: () => void;
  onCloseCallback?: () => void;
};

export const DEFAULT_STATUS_MODAL: TStatusModal = {
  open: false,
  type: 'success',
  message: '',
};

export const EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE: TSignatureValidationDocumentState = {
  hasInvalidSignature: false,
  hasRNOCPPMismatch: false,
  hasNoKep: false,
  hasGeneratedProtocol: false,
};

export const COMPLETED_STATUSES: TApplicationStatus[] = [
  StatusFilterValue.Sent,
  StatusFilterValue.Accepted,
];

export const READ_ONLY_STATUSES: TApplicationStatus[] = [
  StatusFilterValue.Denied,
  StatusFilterValue.PartlyAccepted,
  StatusFilterValue.Sent,
];

export const COVENANTS_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';

export const TABLE_HEADERS = [
  {
    id: 'name',
    label: 'Назва документа',
    align: 'left' as const,
    sx: { pl: 4, width: '28%', fontWeight: 600 },
  },
  {
    id: 'covenants',
    label: 'Ковенанти',
    align: 'center' as const,
    sx: { width: '12%', fontWeight: 600 },
  },
  {
    id: 'comment',
    label: 'Коментар',
    align: 'center' as const,
    sx: { width: '20%', fontWeight: 600 },
  },
  {
    id: 'period',
    label: 'Період / рік',
    align: 'center' as const,
    sx: { width: '16%', fontWeight: 600 },
  },
  {
    id: 'status',
    label: 'Статус',
    align: 'center' as const,
    sx: { width: '150px', fontWeight: 600 },
  },
  {
    id: 'actions',
    label: 'Дії',
    align: 'right' as const,
    sx: { width: '280px', pr: 4, fontWeight: 600 },
  },
];

export const APP_STATUS_OPTIONS: {
  value: StatusFilterValue;
  label: string;
}[] = [
  { value: StatusFilterValue.All, label: 'Всі статуси' },
  ...Object.entries(APP_STATUS_MAP).map(([value, { label }]) => ({
    value: value as StatusFilterValue,
    label,
  })),
];

export const COLUMN_TOOLTIPS: Record<string, string> = {
  action: "Перегляд пов'язаних ковенантів для вибраного документа",
  document: 'Документи, які потрібно надати в Банк',
  deadline: 'Кінцева дата надання документу по умовам',
  period: 'Назва періоду, за який необхідно надати документи',
};

export const buildColumns = (
  onOpenCovenants: (doc: TApiRequiredDocument) => void
): TColumnDef<TApiRequiredDocument>[] => [
  {
    key: 'document',
    header: 'Документи',
    align: 'left',
    render: (row) => (
      <Box sx={covConstantsStyles.docInfo}>
        <Typography
          variant="body2"
          sx={mergeSx(
            covConstantsStyles.tablePrimaryText,
            covConstantsStyles.tablePrimaryTextMultiLine
          )}
        >
          {row.displayName}
        </Typography>
      </Box>
    ),
  },
  {
    key: 'period',
    header: 'Період',
    align: 'left',
    render: (row) => {
      const periodString = [row.childPeriod, row.yearPeriod].filter(Boolean).join(' ');

      return <Typography sx={covConstantsStyles.periodText}>{periodString || '-'}</Typography>;
    },
  },
  {
    key: 'deadline',
    header: 'Термін',
    align: 'left',
    render: (row) => {
      let displayDate = '-';
      let pastDeadline = false;

      if (row.deadline) {
        displayDate = formatDateForDisplay(row.deadline) || row.deadline;
        pastDeadline = isPastDeadline(row.deadline);
      }

      return (
        <Typography sx={covConstantsStyles.deadlineText(pastDeadline)}>{displayDate}</Typography>
      );
    },
  },
  {
    key: 'status',
    header: 'Статус',
    align: 'left',
    render: (row) => {
      const { label, color, description } = getDocumentStatusConfig(row.status);

      return (
        <StatusChipWithTooltip
          label={label}
          color={color}
          size="small"
          description={description}
          chipSx={mergeSx(
            covConstantsStyles.statusChip,
            row.status === 'IN_PROGRESS' ? covConstantsStyles.processingStatusChip : null
          )}
        />
      );
    },
  },
  {
    key: 'action',
    header: 'Дія',
    align: 'left',
    render: (row) => (
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onOpenCovenants(row);
        }}
        sx={covConstantsStyles.covBtn}
      >
        Переглянути ковенант
      </Button>
    ),
  },
];

export const MAX_FILE_SIZE = 15 * 1024 * 1024;
export const ALLOWED_UPLOAD_FILE_EXTENSIONS: string[] = [
  '7z',
  'bmp',
  'csv',
  'doc',
  'docx',
  'jpeg',
  'jpg',
  'm4a',
  'mp3',
  'mp4',
  'msg',
  'p7s',
  'pdf',
  'png',
  'ppt',
  'pptx',
  'rar',
  'rtf',
  'tiff',
  'tmp',
  'txt',
  'xls',
  'xlsx',
  'xml',
  'zip',
];
export const EMPTY_FILE_ERROR_MESSAGE = 'Файл пустий. Завантажте, будь ласка, інший файл.';
export const FILE_TOO_LARGE_ERROR_MESSAGE = `${errorMessages.FILE_TOO_LARGE}. Завантажте, будь ласка, інший файл.`;
export const UNSUPPORTED_FILE_EXTENSION_ERROR_MESSAGE =
  'Це розширення файлу завантажити неможливо.';

export const ACTION_TOOLTIP = 'Перегляд деталей заявки';

export const DEFAULT_DOCUMENTS_PAGE = 0;
export const DEFAULT_DOCUMENTS_ROWS_PER_PAGE = 10;
export const COVENANTS_GET_STARTED_PATH = '/covenants/get-started';

export const covenantsBackdropSx = (
  theme: Theme,
  options?: {
    isBlocking?: boolean;
  }
) => ({
  color: '#fff',
  zIndex: theme.zIndex.modal + (options?.isBlocking ? 2 : 1),
  ...(options?.isBlocking ? { backgroundColor: 'rgba(0, 0, 0, 0.45)' } : {}),
});
